<script>
import StaffCard from '../../staffs/StaffCard.vue';
import DisplayModeToggle from 'dashboard/components/dahreply/DisplayModeToggle.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import { debounce } from '@chatwoot/utils';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import StaffTable from '../../staffs/StaffTable.vue';
import LinkStaffModal from '../LinkStaffModalV2.vue';
import LinkStaffServiceModal from '../LinkStaffServiceModal.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'StaffTab',
  components: {
    StaffCard,
    DisplayModeToggle,
    LoadingState,
    ErrorState,
    EmptyState,
    PaginationBar,
    StaffTable,
    LinkStaffModal,
    LinkStaffServiceModal,
  },
  created() {
    this.debouncedUpdateQuery = debounce((value) => {
      this.$store.dispatch('staffV2/updateFiltersAndFetch', { q: value });
    }, 300);
  },
  computed: {
    isOnboarding() {
      return this.$route.query.onboarding === 'true';
    },
    serviceOptions() {
      return this.$store.state.branchServices.allBranchServices;
    },
    branchId() {
      return this.$route.params.branchId;
    },
    staffList() {
      return this.$store.state.staffV2.staffList;
    },
    isLoading() {
      return this.$store.state.staffV2.isLoading;
    },
    isError() {
      return this.$store.state.staffV2.isError;
    },
    pagination() {
      return this.$store.getters['staffV2/formattedPagination'];
    },
    filters() {
      return this.$store.state.staffV2.filters;
    },
    filterService: {
      get() {
        return this.filters.service_id;
      },
      set(value) {
        this.$store.dispatch('staffV2/updateFiltersAndFetch', { service_id: value });
      },
    },
    filterActive: {
      get() {
        return this.filters.active;
      },
      set(value) {
        this.$store.dispatch('staffV2/updateFiltersAndFetch', { active: value });
      },
    },
    filterQuery: {
      get() {
        return this.filters.q;
      },
      set(value) {
        this.debouncedUpdateQuery(value);
      }
    },
    isFilterActive() {
      return this.filterService || this.filterActive !== null || this.filterQuery;
    },
    emptyStateMessage() {
      if (this.isFilterActive) {
        return 'No staffs match your current filters.';
      }
      return 'Try creating a new staff to get started.';
    },
  },
  data(){
    return {
      isAddingStaff: false,
      displayMode: 'card',
      debouncedUpdateQuery: null,
      showLinkModal: false,
      showLinkServiceModal: false,
      selectedStaffForLinking: null,
    }
  },
  methods: {
    async refreshPage() {
      try {
        await this.$store.dispatch('staffV2/updateFiltersAndFetch', { branch_id: this.branchId });
      } catch(err) {
        console.error(err);
        useAlert(`Failed to fetch staff: ${err}`);
      }
    },
    async fetchServiceOptions() {
      try {
        await this.$store.dispatch('branchServices/fetchAllBranchServices');
      } catch(err) {
        console.error(err);
        useAlert(`Failed to fetch service options: ${err}`);
      }
    },
    fetchPreviousPage() {
      if (this.pagination.currentPage > 1) {
        let prev = this.pagination.currentPage - 1;
        this.$store.dispatch('staffV2/updatePaginationAndFetch', { page: prev });
      }
    },
    fetchNextPage() {
      if (this.pagination.currentPage < this.pagination.totalPages) {
        let next = this.pagination.currentPage + 1;
        this.$store.dispatch('staffV2/updatePaginationAndFetch', { page: next });
      }
    },
    updatePageSize(newSize) {
      this.$store.dispatch('staffV2/updatePaginationAndFetch', { page: 1, page_size: newSize });
    },
    handleNavigate(id) {
      this.$router.push({
        name: 'organization.branches.details.staff',
        params: { staffId: id }
      });
    },
    async clearFilters() {
      await this.$store.dispatch('staffV2/updateFiltersAndFetch', { 
        branch_id: this.branchId, service_id: null, active: null, q: null 
      });
    },
    handleCloseLink() {
      this.showLinkModal = false;
    },
    handleOpenLinkService({ staffId, services }) {
      this.selectedStaffForLinking = { staffId, services };
      this.showLinkServiceModal = true;
    },
    handleCloseLinkService() {
      this.showLinkServiceModal = false;
      this.selectedStaffForLinking = null;
    }
  },
  async mounted() {
    await this.refreshPage();
    await this.fetchServiceOptions();
  }
}
</script>

<template>
  <div class="px-8 pb-4 overflow-y-auto">
    <!-- Modals -->
    <LinkStaffModal v-if="showLinkModal" :staffs="staffList" @close="handleCloseLink" @refresh="refreshPage" />
    <LinkStaffServiceModal 
      v-if="showLinkServiceModal && selectedStaffForLinking" 
      :services="selectedStaffForLinking.services"
      :staff-id="selectedStaffForLinking.staffId"
      :branch-id="branchId"
      @close="handleCloseLinkService" 
      @refresh="refreshPage" 
    />

    <!-- Toolbar -->
    <div class="w-full flex justify-end gap-4 mb-4">
      <DisplayModeToggle
        :modelValue="displayMode"
        @update:modelValue="displayMode = $event"
      />
      <woot-button color-scheme="secondary" icon="person" @click="$router.push({ name: 'organization.staffs' })">
        Manage Staffs
      </woot-button>
      <div class="relative inline-block">
        <woot-button icon="add" @click="showLinkModal = true">
          Add Staff to Branch
        </woot-button>
        <!-- Onboarding Hint Tooltip -->
        <div v-if="isOnboarding" class="absolute -bottom-10 right-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
          <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
            Hint: Click to link staff to this branch <fluent-icon icon="caret-up" />
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="w-full mb-4 flex gap-4 items-end">
      <input 
        v-model="filterQuery" 
        placeholder="Enter your query here..."
        class="border px-2 py-1.5 border-slate-200 rounded-md flex-1"
      />

      <div class="flex-1">
        <label class="text-sm">Service</label>
        <select v-model="filterService" class="mb-0">
          <option :value="null">All</option>
          <option v-for="option in serviceOptions" :key="option.service.id" :value="option.service.id">
            {{ option.service.name }}
          </option>
        </select>
      </div>

      <div class="flex-1">
        <label class="text-sm">Active Status</label>
        <select v-model="filterActive" class="mb-0">
          <option :value="null">All</option>
          <option :value="true">Active</option>
          <option :value="false">Inactive</option>
        </select>
      </div>

      <woot-button color-scheme="secondary" @click="clearFilters" :disabled="!isFilterActive">Clear Filters</woot-button>
    </div>

    <PaginationBar
      v-if="!isLoading && !isError && staffList.length !== 0"
      :pagination="pagination"
      :loading="isLoading"
      @previous="fetchPreviousPage"
      @next="fetchNextPage"
      @page-size-change="updatePageSize"
    />

    <LoadingState v-if="isLoading" message="Loading staffs..." />

    <ErrorState v-else-if="!isLoading && isError" />

    <EmptyState
      v-else-if="!isLoading && !isError && staffList.length === 0"
      title="No Staff Found"
      :message="emptyStateMessage"
    />

    <!-- Display Modes -->
    <div
      v-else-if="displayMode === 'card' && !isLoading && !isError"
      class="grid grid-cols-1 md:grid-cols-3 gap-4 overflow-y-auto"
    >
      <StaffCard
        v-for="(staff, index) in staffList"
        :key="staff.staff_id"
        :staff="staff"
        :show-actions="false"
        :branchId="branchId"
        :isBranch="true"
        :isFirst="index === 0"
        @navigate="handleNavigate"
        @refresh="refreshPage"
        @open-link-modal="handleOpenLinkService"
      />
    </div>

    <StaffTable
      v-else-if="displayMode === 'table' && !isLoading && !isError"
      :staff-list="staffList"
      :show-actions="false"
      :isBranch="true"
      :branchId="branchId"
      @navigate="handleNavigate"
      @open-link-modal="handleOpenLinkService"
    />
  </div>
</template>

<style scoped>
@keyframes bounce-subtle {
  0%, 100% {
    transform: translateY(0) translateX(4px);
  }
  50% {
    transform: translateY(-5px) translateX(4px);
  }
}

.animate-bounce-subtle {
  animation: bounce-subtle 2s ease-in-out infinite;
}
</style>